import { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticles } from "@/hooks/useArticles";

const destinationColors: Record<string, string> = {
  USA: "bg-blue-600",
  France: "bg-blue-500",
  UK: "bg-red-600",
  Canada: "bg-red-500",
};

function getDestinationBadge(country: string): string {
  for (const key of Object.keys(destinationColors)) {
    if (country.includes(key)) return destinationColors[key];
  }
  return "bg-gray-600";
}

function getDestinationLabel(country: string, fallback: string): string {
  return country.split("→")[1]?.trim() || fallback;
}

export default function DiasporaSection() {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { articles: diaspora, loading } = useArticles({ category: "Diaspora Africaine", limit: 10 });

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "right" ? 400 : -400, behavior: "smooth" });
    }
  };

  const featured = diaspora[0];
  const grid = diaspora.slice(1, 5);
  const scrollList = diaspora;
  const worldLabel = t("diaspora.world");

  return (
    <section className="py-16 bg-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://public.readdy.ai/ai/img_res/1b75194bb36d8b1bb579fd20c4aa5c67.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-0 right-0 w-96 h-96 opacity-10 pointer-events-none">
        <div className="w-full h-full rounded-full border-2 border-orange-400"></div>
        <div className="absolute inset-8 rounded-full border border-orange-400"></div>
        <div className="absolute inset-16 rounded-full border border-orange-400"></div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="w-10 h-1 rounded-full bg-orange-500 mb-3"></div>
            <h2 className="text-2xl md:text-3xl font-bold text-white font-serif">{t("section.diaspora.title")}</h2>
            <p className="text-gray-400 mt-1 text-sm md:text-base">{t("section.diaspora.subtitle")}</p>
          </div>
          <Link
            to="/diaspora-africaine"
            className="flex items-center gap-2 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("section.viewAll")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1 bg-gray-800 rounded-2xl h-96 animate-pulse"></div>
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-2xl h-44 animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {featured && (
                <Link
                  to={`/article/${featured.id}`}
                  className="lg:col-span-1 group relative rounded-2xl overflow-hidden cursor-pointer block"
                  style={{ minHeight: "420px" }}
                >
                  <img src={featured.image} alt={featured.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10"></div>
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Diaspora</span>
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full">{featured.countryFlag}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 text-orange-400 text-xs mb-2">
                      <i className="ri-map-pin-2-line"></i>
                      <span>{featured.country}</span>
                      <span className="mx-1">·</span>
                      <span>{featured.sector}</span>
                    </div>
                    <h3 className="text-white font-bold text-lg leading-snug line-clamp-3 mb-3 group-hover:text-orange-300 transition-colors">{featured.title}</h3>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4">{featured.summary}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <i className="ri-user-line text-orange-400 text-xs"></i>
                        </div>
                        <span className="text-gray-300 text-xs">{featured.entrepreneur}</span>
                      </div>
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-orange-500 text-white group-hover:bg-orange-400 transition-colors">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {grid.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group relative rounded-2xl overflow-hidden cursor-pointer block"
                    style={{ minHeight: "190px" }}
                  >
                    <img src={article.image} alt={article.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                    <div className="absolute top-3 left-3">
                      <span className={`text-white text-xs font-bold px-2 py-0.5 rounded-full ${getDestinationBadge(article.country)}`}>
                        {getDestinationLabel(article.country, worldLabel)}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="text-orange-400 text-xs mb-1 flex items-center gap-1">
                        <span>{article.countryFlag}</span>
                        <span>{article.sector}</span>
                      </div>
                      <h3 className="text-white font-bold text-sm leading-snug line-clamp-2 group-hover:text-orange-300 transition-colors">{article.title}</h3>
                      <p className="text-gray-400 text-xs mt-1 line-clamp-1">{article.entrepreneur}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Horizontal scroll */}
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-500 text-sm">{t("diaspora.alsoIn")}</p>
                <div className="flex gap-2">
                  <button onClick={() => scroll("left")} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-800 border border-gray-700 text-gray-400 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors cursor-pointer">
                    <i className="ri-arrow-left-s-line text-lg"></i>
                  </button>
                  <button onClick={() => scroll("right")} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-800 border border-gray-700 text-gray-400 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </button>
                </div>
              </div>

              <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {scrollList.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group flex-shrink-0 w-72 bg-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer block"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-sm">{article.countryFlag}</span>
                        <i className="ri-arrow-right-line text-orange-400 text-xs"></i>
                        <span className="text-white text-xs font-medium">{getDestinationLabel(article.country, worldLabel)}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <span className="text-xs font-semibold text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full">{article.sector}</span>
                      <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2 mt-2 mb-1 group-hover:text-orange-300 transition-colors">{article.title}</h3>
                      <p className="text-gray-500 text-xs line-clamp-2">{article.summary}</p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700">
                        <span className="text-gray-500 text-xs flex items-center gap-1">
                          <i className="ri-user-line"></i>{article.entrepreneur}
                        </span>
                        {(article.views ?? 0) > 0 && (
                          <span className="text-gray-600 text-xs flex items-center gap-1">
                            <i className="ri-eye-line"></i>{((article.views ?? 0) / 1000).toFixed(0)}K
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}

        {/* CTA Banner */}
        <div className="mt-10 bg-gradient-to-r from-orange-500/20 to-yellow-500/10 border border-orange-500/20 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-500/20 flex-shrink-0">
              <i className="ri-earth-line text-orange-400 text-2xl"></i>
            </div>
            <div>
              <h3 className="text-white font-bold text-base">{t("diaspora.cta.title")}</h3>
              <p className="text-gray-400 text-sm">{t("diaspora.cta.desc")}</p>
            </div>
          </div>
          <Link
            to="/soumettre"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-circle-line"></i>
            {t("diaspora.cta.btn")}
          </Link>
        </div>
      </div>
    </section>
  );
}
